<script setup lang="ts">
import {
  ref as firebaseRef,
  uploadBytesResumable,
  deleteObject,
  getDownloadURL
} from "firebase/storage";
import { Ref } from "vue";
import VueCropper from "vue-cropperjs";
import trashWhite from "~/assets/graphics/icons/cd-trash-white.svg";
import ModalBase from "~/components/Modal/ModalBase.vue";
import "cropperjs/dist/cropper.css";

// item is an image to upload
const props = defineProps({
  item: {
    type: Object as PropType<{ imageUrl: string; uploadUri: string }>
  },
  showDeleteImage: {
    type: Boolean,
    required: false,
    default: false
  },
  title: {
    type: String,
    required: false,
    default: () => {
      const { t } = useI18n();
      return t("main_menu.change_image");
    }
  }
});
// emit on component to close or submit form modal
const emit = defineEmits(["close", "submit"]);
//
const storage = useNuxtApp().$imageStorage as any;

const imageFile: Ref<File | undefined> = ref(undefined);
const currentImageUrl = computed(() => {
  if (props.item?.imageUrl) {
    return props.item.imageUrl;
  }

  return null;
});
const imageUrl = computed(() => {
  if (!isValidImage(imageFile.value)) {
    return currentImageUrl.value;
  }

  return URL.createObjectURL(imageFile.value as File);
});

const isValidImage = (imageFile?: File) => {
  if (!imageFile) {
    return false;
  }

  const type = imageFile.type;
  return type.startsWith("image/");
};

const revokeUrl = (imageUrl: string) => {
  if (imageUrl) {
    setTimeout(() => {
      URL.revokeObjectURL(imageUrl);
    }, 1000);
  }
};

const imageHasBeenChanged = ref(false);

const filesChanged = (newFiles: FileList) => {
  if (newFiles.length === 1) {
    const image = newFiles[0];

    if (!isValidImage(image)) {
      imageFile.value = undefined;
      imageHasBeenChanged.value = false;
      return;
    }

    convertToPngIfNeeded(image).then((imageAsPNG) => {
      imageFile.value = imageAsPNG;
      imageHasBeenChanged.value = true;
    });
  } else {
    imageHasBeenChanged.value = false;
  }
};

const changeFileEndingToPNG = (fileName: string) => {
  const parts = fileName.split(".");
  parts[parts.length - 1] = "png";
  return parts.join(".");
};

const convertToPngIfNeeded: (image: File) => Promise<File> = (image: File) =>
  new Promise((resolve) => {
    const type = image.type;

    if (type !== "image/png") {
      // convert to png
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.src = URL.createObjectURL(image);
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          const newFileName = changeFileEndingToPNG(image.name);
          canvas.remove();
          resolve(
            new File([blob as BlobPart], newFileName, { type: "image/png" })
          );
        }, "image/png");
      };
    } else {
      resolve(image);
    }
  });

const uploadInProgress: Ref<boolean> = ref(false);
const cropperRef = ref<any>(null);

const uploadImage = async () => {
  if (!imageFile.value || !isValidImage(imageFile.value)) {
    emit("close");
  }

  uploadInProgress.value = true;

  const cropper = cropperRef.value;

  // Check if a cropper instance exists
  if (!cropper) {
    emit("close");
    return;
  }

  // Get the cropped image data from Vue Cropper
  const croppedCanvas = cropper.getCroppedCanvas();

  // Check if a canvas with cropped image data exists
  if (!croppedCanvas) {
    // Handle if no image is cropped
    emit("close");
    return;
  }

  // Convert the cropped canvas to a blob
  croppedCanvas.toBlob((blob: any) => {
    const croppedImage = new File([blob], imageFile.value!.name, {
      type: "image/png"
    });

    // Perform the upload to Firebase Storage
    const storageRef = firebaseRef(storage, props.item?.uploadUri);
    const uploadTask = uploadBytesResumable(storageRef, croppedImage);

    uploadTask.on(
      "state_changed",
      null,
      (error) => {
        console.error(error);
        // TODO: handle error
        emit("close");
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

        // Update the image URL to the new download URL
        if (props.item) {
          props.item.imageUrl = downloadURL;
        }

        // Revoke the old URL
        revokeUrl(props.item?.imageUrl as string);

        // Emit the submit event to indicate successful upload
        emit("submit");
      }
    );
  }, "image/png");
};

const cropData = ref({ x: 0, y: 0, width: 0, height: 0 });
const handleCropChange = (event: any) => {
  cropData.value = event.detail;
};

const deleteImage = async () => {
  const storageRef = firebaseRef(storage, props.item?.uploadUri);

  try {
    await deleteObject(storageRef);
    emit("submit");
    return Promise.resolve();
  } catch (error) {
    emit("close");
    return Promise.reject(error);
  }
};
</script>

<template>
  <ModalBase
    class="modal-title-image-upload"
    :title="props.title"
    :submit-disabled="!imageFile"
    :upload-in-progress="uploadInProgress"
    @close="() => emit('close')"
    @submit="uploadImage"
  >
    <div class="dropzone-container">
      <VueCropper
        v-if="imageUrl && imageHasBeenChanged"
        ref="cropperRef"
        :src="imageUrl"
        class="image-preview"
        alt="Bildvorschau"
        @crop="handleCropChange"
      />
      <img
        v-if="imageUrl && !imageHasBeenChanged"
        :src="imageUrl"
        class="image-preview"
        alt="Bildvorschau"
      >
      <div
        v-if="props.showDeleteImage"
        class="delete-background"
        @click="deleteImage"
      >
        <img :src="trashWhite">
      </div>
      <FileDropZone
        class="dropzone"
        single-file-only
        accept=".jpg,.jpeg,.png,.gif,.webp"
        @files-changed="filesChanged"
      />
    </div>
  </ModalBase>
</template>

<style scoped>
.image-preview {
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  object-fit: contain;
  border-radius: 0.375rem 0.375rem 0 0;
}

.dropzone-container {
  border-radius: 0.375rem;
  overflow: hidden;
}

.delete-background {
  background-color: var(--ct_E2);
  padding: 1rem 1.5rem;
  max-width: 68px;
  border-radius: 0 0 0 50px;
  position: absolute;
  right: 40px;
  top: 20px;
  cursor: pointer;
}
</style>
